import React from 'react';
import { Spin, Skeleton, Card } from 'antd';
import { motion } from 'framer-motion';
import { LoadingOutlined } from '@ant-design/icons';

const LoadingOutlinedIcon = <LoadingOutlined style={{ fontSize: 24, color: 'var(--primary-purple)' }} spin />;

// Modern loading spinner with animation
export const ModernSpinner = ({ size = 'large', tip = 'Loading...' }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--spacing-2xl)',
    }}
  >
    <div style={{
      background: 'var(--gradient-card)',
      border: '1px solid var(--border-light)',
      borderRadius: 'var(--radius-2xl)',
      padding: 'var(--spacing-2xl)',
      boxShadow: 'var(--shadow-card)',
      backdropFilter: 'blur(20px)',
    }}>
      <Spin indicator={LoadingOutlinedIcon} size={size} tip={tip} />
    </div>
  </motion.div>
);

// Skeleton for dashboard cards
export const DashboardCardSkeleton = ({ count = 6 }) => (
  <div style={{ padding: 'var(--spacing-xl)', minHeight: '100vh' }}>
    {/* Header Skeleton */}
    <Card
      style={{
        background: 'var(--gradient-card)',
        border: '1px solid var(--border-light)',
        borderRadius: 'var(--radius-2xl)',
        marginBottom: 'var(--spacing-2xl)',
      }}
      bodyStyle={{ padding: 'var(--spacing-xl)' }}
    >
      <Skeleton
        active
        avatar={{ size: 'large', shape: 'square' }}
        title={{ width: '60%' }}
        paragraph={{ rows: 2, width: ['40%', '80%'] }}
      />
    </Card>

    {/* Metrics Cards Skeleton */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--spacing-xl)', marginBottom: 'var(--spacing-2xl)' }}>
      {Array.from({ length: count }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1, duration: 0.5 }}
        >
          <Card
            style={{
              background: 'var(--gradient-card)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-card)',
            }}
            bodyStyle={{ padding: 'var(--spacing-xl)' }}
          >
            <Skeleton
              active
              avatar={{ size: 'large', shape: 'circle' }}
              title={{ width: '70%' }}
              paragraph={{ rows: 2, width: ['50%', '90%'] }}
            />
          </Card>
        </motion.div>
      ))}
    </div>

    {/* Charts Skeleton */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 'var(--spacing-xl)' }}>
      {Array.from({ length: 2 }).map((_, index) => (
        <Card
          key={index}
          style={{
            background: 'var(--gradient-card)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-card)',
          }}
          bodyStyle={{ padding: 'var(--spacing-xl)' }}
        >
          <Skeleton
            active
            title={{ width: '40%' }}
            paragraph={{ rows: 8, width: ['100%', '95%', '90%', '85%', '80%', '75%', '70%', '65%'] }}
          />
        </Card>
      ))}
    </div>
  </div>
);

// Table skeleton
export const TableSkeleton = ({ columns = 5, rows = 10 }) => (
  <Card
    style={{
      background: 'var(--gradient-card)',
      border: '1px solid var(--border-light)',
      borderRadius: 'var(--radius-xl)',
      boxShadow: 'var(--shadow-card)',
    }}
    bodyStyle={{ padding: 0 }}
  >
    {/* Table Header Skeleton */}
    <div style={{
      padding: 'var(--spacing-lg) var(--spacing-xl)',
      borderBottom: '1px solid var(--border-light)',
      background: 'var(--gradient-card)',
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: 'var(--spacing-md)',
    }}>
      {Array.from({ length: columns }).map((_, index) => (
        <Skeleton key={index} active title={{ width: '80%' }} paragraph={false} />
      ))}
    </div>

    {/* Table Body Skeleton */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <motion.div
        key={rowIndex}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: rowIndex * 0.05, duration: 0.3 }}
        style={{
          padding: 'var(--spacing-lg) var(--spacing-xl)',
          borderBottom: rowIndex < rows - 1 ? '1px solid var(--border-light)' : 'none',
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 'var(--spacing-md)',
          alignItems: 'center',
        }}
      >
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton
            key={colIndex}
            active
            title={false}
            paragraph={{ rows: 1, width: colIndex === 0 ? '60%' : '90%' }}
          />
        ))}
      </motion.div>
    ))}

    {/* Pagination Skeleton */}
    <div style={{
      padding: 'var(--spacing-lg) var(--spacing-xl)',
      borderTop: '1px solid var(--border-light)',
      background: 'var(--dark-bg-card)',
      borderRadius: '0 0 var(--radius-xl) var(--radius-xl)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <Skeleton active title={false} paragraph={{ rows: 1, width: '200px' }} />
      <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} active title={false} paragraph={{ rows: 1, width: '32px' }} />
        ))}
      </div>
    </div>
  </Card>
);

// Form loading state
export const FormSkeleton = () => (
  <Card
    style={{
      background: 'var(--gradient-card)',
      border: '1px solid var(--border-light)',
      borderRadius: 'var(--radius-2xl)',
      boxShadow: 'var(--shadow-card)',
    }}
    bodyStyle={{ padding: 'var(--spacing-2xl)' }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
      <Skeleton active avatar title={{ width: '30%' }} paragraph={false} />

      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index}>
          <Skeleton active title={{ width: '25%' }} paragraph={false} />
          <Skeleton active title={false} paragraph={{ rows: 1, width: '100%' }} />
        </div>
      ))}

      <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end' }}>
        <Skeleton active title={false} paragraph={{ rows: 1, width: '100px' }} />
        <Skeleton active title={false} paragraph={{ rows: 1, width: '100px' }} />
      </div>
    </div>
  </Card>
);

// Loading overlay for existing content
export const LoadingOverlay = ({ loading, children, tip = 'Loading...' }) => (
  <div style={{ position: 'relative' }}>
    {children}
    {loading && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          borderRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{
          background: 'var(--gradient-card)',
          border: '1px solid var(--border-light)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-xl)',
          boxShadow: 'var(--shadow-card)',
        }}>
          <Spin indicator={LoadingOutlinedIcon} tip={tip} />
        </div>
      </motion.div>
    )}
  </div>
);

export default ModernSpinner;
